import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import Sidebar from '../components/Sidebar';
import { Users, Calendar, Camera, Target, BarChart2, Settings } from 'react-feather';
import PlanUsage from '../components/PlanUsage';
import useUsageNotifications from '../hooks/useUsageNotifications';
import './Dashboard.css';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminData, setAdminData] = useState(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();

  const logoUrl = theme === 'light' 
    ? 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7ade8283bc1015dd30.webp'
    : 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7a306550caec6c2c16.webp';

  useEffect(() => {
    const handleSidebarToggle = () => {
      const collapsed = localStorage.getItem('sidebarCollapsed') === 'true';
      setSidebarCollapsed(collapsed);
    };

    window.addEventListener('sidebarToggle', handleSidebarToggle);
    return () => window.removeEventListener('sidebarToggle', handleSidebarToggle);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored || stored === 'undefined') {
      navigate('/');
      return;
    }
    let userData;
    try {
      userData = JSON.parse(stored);
    } catch (e) {
      navigate('/');
      return;
    }
    setAdminData(userData);
    fetchStats();
    fetchUsage();

  }, [navigate]);

  // Refresh data when location changes (e.g., returning from creating an event)
  useEffect(() => {
    console.log('🔄 Location changed - refreshing dashboard data');
    fetchStats();
    fetchUsage();
  }, [location.key]); // location.key changes on navigation

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://live-event-photos.onrender.com/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({
        totalClients: 0,
        totalEvents: 0,
        totalPhotos: 0,
        activeEvents: 0
      });
    }
  };

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 Fetching usage with token:', token ? 'exists' : 'missing');
      
      const response = await fetch('https://live-event-photos.onrender.com/api/admin/usage', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('📊 Usage API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Usage API error:', response.status, errorText);
        
        // If 404, admin doesn't have a venue yet (legacy admin)
        if (response.status === 404) {
          console.log('ℹ️ No venue found for this admin');
          setUsage(null);
          return;
        }
        throw new Error(`Failed to fetch usage: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Usage data received:', data);
      setUsage(data);
    } catch (error) {
      console.error('💥 Error fetching usage:', error);
      // Set to null for legacy admins without venues
      setUsage(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/');
  };

  const handleBuyAddon = () => {
    alert('To purchase event add-ons, please contact support at support@eventpiclive.com');
  };

  const isSuperAdmin = adminData?.role === 'super_admin';
  const firstName = adminData?.full_name?.split(' ')[0] || adminData?.email?.split('@')[0] || 'User';

  // Show usage notifications
  useUsageNotifications(usage);

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <ThemeToggle />
      <Sidebar onLogout={handleLogout} isSuperAdmin={isSuperAdmin} />

      <div className="dashboard-home">
        {loading ? (
          <div className="loading-content">
            <div className="spinner"></div>
            <p>Loading dashboard...</p>
          </div>
        ) : (
          <>
        <h1>Dashboard Overview</h1>

        {stats && (
          <div className="stats-grid">
            {isSuperAdmin && (
              <div className="stat-card">
                <div className="stat-icon"><Users size={32} /></div>
                <div className="stat-details">
                  <div className="stat-value">{stats.totalClients || 0}</div>
                  <div className="stat-label">Total Clients</div>
                </div>
              </div>
            )}

            <div className="stat-card">
              <div className="stat-icon"><Calendar size={32} /></div>
              <div className="stat-details">
                <div className="stat-value">{stats.totalEvents || 0}</div>
                <div className="stat-label">{isSuperAdmin ? 'All Events' : 'My Events'}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon"><Camera size={32} /></div>
              <div className="stat-details">
                <div className="stat-value">{stats.totalPhotos || 0}</div>
                <div className="stat-label">Photos Uploaded</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon"><Target size={32} /></div>
              <div className="stat-details">
                <div className="stat-value">{stats.activeEvents || 0}</div>
                <div className="stat-label">Active Events</div>
              </div>
            </div>
          </div>
        )}

        {!isSuperAdmin && usage && usage.plan && (
          <div className="usage-section">
            <div className="section-header-with-badge">
              <h2>Plan & Usage</h2>
              <div className="plan-badge" data-plan={usage.plan.toLowerCase()}>
                {usage.plan} Plan
              </div>
              {usage.plan !== 'Enterprise' && (
                <button className="upgrade-btn-header" onClick={() => window.open('https://eventpiclive.com/pricing', '_blank')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                    <polyline points="17 6 23 6 23 12"></polyline>
                  </svg>
                  Upgrade Plan
                </button>
              )}
            </div>
            <PlanUsage 
              usage={usage}
              onUpgrade={() => window.open('https://eventpiclive.com/pricing', '_blank')}
              onBuyAddon={() => handleBuyAddon()}
            />
          </div>
        )}

        <div className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="action-buttons">
            <button onClick={() => navigate('/events')} className="btn-primary">
              <BarChart2 size={18} /> View Events
            </button>
            <button onClick={() => navigate('/admin/settings')} className="btn-secondary">
              <Settings size={18} /> Settings
            </button>
          </div>
        </div>
          </>
        )}
      </div>

      
    </div>
  );
}

export default Dashboard;
